import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Globe, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LOCALES, changeLocale } from '../i18n'
import { useAuth } from '../contexts/AuthContext'
import { kanbify } from '../lib/kanbifyClient'

export default function LanguageSwitcher({ variant = 'sidebar' }) {
    const { i18n, t } = useTranslation()
    const { user, profile } = useAuth() || {}
    const [open, setOpen] = useState(false)
    const [coords, setCoords] = useState({ top: 0, left: 0 })
    const triggerRef = useRef(null)
    const dropdownRef = useRef(null)

    // Sync profile.preferred_language → i18n ONLY when the profile's stored
    // preference changes (initial load / external change). Must NOT depend on
    // i18n.language: a manual switch changes i18n.language, which (if it were
    // a dep) re-ran this effect while `profile` still held the stale old
    // language (persistLocale's DB write is async), instantly reverting the
    // user's choice — the "need to refresh to see the change" bug.
    useEffect(() => {
        const pref = profile?.preferred_language
        if (pref && ['ro', 'en', 'zh'].includes(pref) && pref !== i18n.language) {
            changeLocale(pref)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.preferred_language])

    const persistLocale = async (code) => {
        if (!user?.id) return
        try {
            await kanbify.from('profiles').update({ preferred_language: code }).eq('id', user.id)
        } catch {
            // Silent fallback if column missing pre-migration.
        }
    }

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        const DROPDOWN_H = 168
        const DROPDOWN_W = 180
        // Variant 'nav' (inside sidebar): open to the RIGHT of trigger, aligned top
        // Variant 'sidebar'/'auth' (small button): open below trigger
        let top, left
        if (variant === 'nav') {
            top = rect.top
            left = rect.right + 8
            // Flip to left if not enough room
            if (left + DROPDOWN_W > window.innerWidth - 8) left = rect.left - DROPDOWN_W - 8
            // Adjust vertically if dropdown overflows viewport
            if (top + DROPDOWN_H > window.innerHeight - 8) top = window.innerHeight - DROPDOWN_H - 8
            if (top < 8) top = 8
        } else {
            top = rect.bottom + 6
            left = rect.left
            if (top + DROPDOWN_H > window.innerHeight - 8) top = rect.top - DROPDOWN_H - 6
            if (left + DROPDOWN_W > window.innerWidth - 8) left = window.innerWidth - DROPDOWN_W - 8
        }
        setCoords({ top, left })
    }, [open, variant])

    useEffect(() => {
        if (!open) return
        const onClickOutside = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                setOpen(false)
            }
        }
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
        document.addEventListener('mousedown', onClickOutside)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onClickOutside)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    const current = SUPPORTED_LOCALES.find((l) => l.code === i18n.language) || SUPPORTED_LOCALES[0]

    const TriggerEl = variant === 'nav' ? (
        <button
            type="button"
            ref={triggerRef}
            className="sidebar-nav-item"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={open}
            title={t('language.selectLanguage')}
        >
            <Globe size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>{t('language.label')}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{current.flag} {current.code.toUpperCase()}</span>
        </button>
    ) : (
        <button
            type="button"
            ref={triggerRef}
            className="lang-switcher-trigger"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={open}
            title={current.name}
        >
            <Globe size={16} />
            <span className="lang-switcher-code">{current.flag} {current.code.toUpperCase()}</span>
        </button>
    )

    return (
        <>
            {variant === 'nav' ? TriggerEl : (
                <div className={`lang-switcher lang-switcher-${variant}`}>
                    {TriggerEl}
                </div>
            )}
            {open && createPortal(
                <div
                    className="lang-switcher-dropdown"
                    role="listbox"
                    ref={dropdownRef}
                    style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: 180, bottom: 'auto' }}
                >
                    {SUPPORTED_LOCALES.map((loc) => (
                        <button
                            key={loc.code}
                            type="button"
                            className={`lang-switcher-item ${loc.code === current.code ? 'active' : ''}`.trim()}
                            onClick={() => { changeLocale(loc.code); persistLocale(loc.code); setOpen(false) }}
                            role="option"
                            aria-selected={loc.code === current.code}
                        >
                            <span className="lang-flag">{loc.flag}</span>
                            <span className="lang-name">{loc.name}</span>
                            {loc.code === current.code && <Check size={14} className="lang-check" />}
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </>
    )
}
